import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/daily-attendance-report/DailyAttendanceReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody,
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useDailyAttendanceReportUIContext } from "./DailyAttendanceReportUIContext";
import { DailyAttendanceReportFilter } from "./daily-attendance-report-filter/DailyAttendanceReportFilter";
import { DailyAttendanceReportTable } from "./daily-attendance-report-table/DailyAttendanceReportTable";

export function DailyAttendanceReportCard() {
  // Daily Attendance Report UI Context
  const dailyAttendanceReportUIContext = useDailyAttendanceReportUIContext();
  const dailyAttendanceReportUIProps = useMemo(() => {
    return {
      queryParams: dailyAttendanceReportUIContext.queryParams
    };
  }, [dailyAttendanceReportUIContext]);

  // Getting curret state of Daily Attendance Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.dailyAttendanceReport }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Daily Attendance Report Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    return () => dispatch(actions.cleanStudentsState());
  }, [dispatch]);

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Günlük Yoklama Raporunu Excele Aktarır */
  const exportDailyAttendanceReportToExcel = () => {
    dispatch(actions.getAllDailyAttendanceReport(dailyAttendanceReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [20, 20, 20, 25, 30, 20, 30, 30, 30, 20];
      const columns = ["Tarih", "Ders Saati", "Yoklama Durumu", "Öğrenci Numarası", "Adı Soyadı", "Şube", "Danışman Öğretmenler", "Yoklama Alan Öğretmen", "Veli Adı Soyadı", "Veli Telefonu"];

      entities.forEach(entity => rows.push([
        entity.date,
        entity.courseHour,
        entity.attendanceStatusName,
        entity.studentNumber,
        entity.studentName,
        entity.section,
        entity.advisors,
        entity.teacher,
        entity.parentName,
        entity.parentPhone
      ]));

      const filename = dailyAttendanceReportUIProps.queryParams.term + " - " + (dailyAttendanceReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Günlük Yoklama Raporu";
      const sheetname = "Günlük Yoklama Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  // /** Belli Bir Günlük Yoklama Raporunu PDF e Aktarır */
  const exportDailyAttendanceReportToPDF = () => {
    dispatch(actions.getAllDailyAttendanceReport(dailyAttendanceReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Tarih",
          rows: entities.map(entity => entity.date)
        },
        {
          header: "Ders Saati",
          rows: entities.map(entity => entity.courseHour)
        },
        {
          header: "Yoklama Durumu",
          rows: entities.map(entity => entity.attendanceStatusName)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Danışman Öğretmenler",
          rows: entities.map(entity => entity.advisors)
        },
        {
          header: "Yoklama Alan Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Veli Adı Soyadı",
          rows: entities.map(entity => entity.parentName)
        },
        {
          header: "Veli Telefonu",
          rows: entities.map(entity => entity.parentPhone)
        }
      ];
  
      const filename = dailyAttendanceReportUIProps.queryParams.term + " - " + (dailyAttendanceReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Günlük Yoklama Raporu";
      exportToPDF(views, filename);
    });
  }

  // /** Belli Bir Günlük Yoklama Raporunu Yazdırma Penceresini Açar */
  const printDailyAttendanceReport = () => {
    dispatch(actions.getAllDailyAttendanceReport(dailyAttendanceReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = dailyAttendanceReportUIProps.queryParams.term + " - " + (dailyAttendanceReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Günlük Yoklama Raporu";
      const properties = ["Tarih", "Ders Saati", "Yoklama Durumu", "Öğrenci Numarası", "Adı Soyadı", "Şube", "Danışman Öğretmenler", "Yoklama Alan Öğretmen", "Veli Adı Soyadı", "Veli Telefonu"];
  
      entities.forEach(entity => {
        data.push({
          "Tarih": entity.date,
          "Ders Saati": entity.courseHour,
          "Yoklama Durumu": entity.attendanceStatusName,
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Şube": entity.section,
          "Danışman Öğretmenler": entity.advisors,
          "Yoklama Alan Öğretmen": entity.teacher,
          "Veli Adı Soyadı": entity.parentName,
          "Veli Telefonu": entity.parentPhone
        });
      });
  
      print({ data, properties, header });
    });    
  }

  // /** Belli Bir Günlük Yoklama Raporunu Panoya Kopyalar */
  const copyDailyAttendanceReport = () => {
    dispatch(actions.getAllDailyAttendanceReport(dailyAttendanceReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Tarih", "Ders Saati", "Yoklama Durumu", "Öğrenci Numarası", "Adı Soyadı", "Şube", "Danışman Öğretmenler", "Yoklama Alan Öğretmen", "Veli Adı Soyadı", "Veli Telefonu"];
      const header = dailyAttendanceReportUIProps.queryParams.term + " - " + (dailyAttendanceReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Günlük Yoklama Raporu";
      entities.forEach(entity => rows.push([
        entity.date,
        entity.courseHour,
        entity.attendanceStatusName,
        entity.studentNumber,
        entity.studentName,
        entity.section,
        entity.advisors,
        entity.teacher,
        entity.parentName,
        entity.parentPhone
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Günlük Yoklama Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportDailyAttendanceReportToExcel}
              exportToPDF={exportDailyAttendanceReportToPDF}
              print={printDailyAttendanceReport}
              copy={copyDailyAttendanceReport}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <DailyAttendanceReportFilter />
        <DailyAttendanceReportTable />
      </CardBody>
    </Card>
  );
}